import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { eachDayOfInterval, getDay } from 'date-fns';
import React, { useMemo, useState } from 'react';
import {FormProvider, useForm } from 'react-hook-form';
import { showToastMessage } from 'src/coach/service/coach.utils';
import { Button, InputField, Modal } from 'src/shared';
import { useLoading } from 'src/shared/hooks/use-loading';
import { useModal } from 'src/shared/hooks/use-modal';
import { useAsyncTaskStatus } from '../../class-details/hooks/async-task-status';
import ConfirmConflictingDates from '../../components/conflicting-availability-modal';
import Error from '../../components/error';
import { upsertAvailabilityPeriodMutation } from '../graphql/availability-mutation';
import { formFields, formSchema } from '../helper';

const AddAvailability = ({ actions, title, coachId, editAvailabilityPeriod, refetch,franchises }) => {
  const isEdit = !!editAvailabilityPeriod?.id;
  const periodFranchises = editAvailabilityPeriod?.periodFranchises || [];
  const [selectedDays, setSelectedDays] = useState(
  editAvailabilityPeriod?.dayOfWeek !== null && typeof editAvailabilityPeriod?.dayOfWeek !== "undefined"
    ? [editAvailabilityPeriod.dayOfWeek]
    : []
);
  const { loading: loadingState } = useLoading();
  const { key:conflictingModalKey,isOpen:isOpenConflictingModal, actions:ConflictingModalActions, context:selectedItem  } = useModal();

  const coachAllFranchises = useMemo(()=>{
    const unsortedFranchises = franchises.map(f=>({
      ...f,
      disabled: !f?.settings?.canManageAvailability
    })) || [];	
    return unsortedFranchises.sort((a, b) => {
    if (a.disabled === b.disabled) {
        return a.name.localeCompare(b.name);
    }
    return a.disabled - b.disabled;
});
  },[editAvailabilityPeriod])
  const defaultValues = useMemo(() => {
    if (isEdit) {
      return {
        daysOfTheWeek: [editAvailabilityPeriod?.dayOfWeek],
        endDate: new Date(editAvailabilityPeriod?.endDate).toISOString().split('T')[0],
        endTime: editAvailabilityPeriod?.endTime,
        startDate: new Date(editAvailabilityPeriod?.startDate).toISOString().split('T')[0],
        startTime: editAvailabilityPeriod?.startTime,
        franchiseIds: franchises
                      .filter(f => periodFranchises?.some(p => Number(p.id) === Number(f.id)))
                      .map(f => f.id)
      };
    }
    return {
      daysOfTheWeek: [],
      endDate: new Date().toISOString().split('T')[0],
      endTime: '17:00',
      startDate: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      franchiseIds: coachAllFranchises?.filter(f=>!f.disabled)?.map(f=>f.id)
      
    };
  }, [editAvailabilityPeriod]);

  const formMethods = useForm({
    defaultValues,
    resolver: yupResolver(formSchema(defaultValues)),
    shouldUnregister: false,
  });

  const { formState: { errors }, getValues, register } = formMethods;
  const [upsertAvailabilityPeriod, { loading: upsertAvailabilityPeriodLoading }] = useMutation(upsertAvailabilityPeriodMutation);

  
  const { asyncTaskExtraData, setAsyncTaskExtraData, setAsyncTaskId } = useAsyncTaskStatus({
    onComplete: async (data) => {
      const response = data?.result;
      const { action, values } = asyncTaskExtraData;
      if (response?.conflictingPeriods?.length > 0) {
        ConflictingModalActions.open({
          action,
          conflictingPeriods: response?.conflictingPeriods,
          values,
        });
        loadingState.stop();
        return;
      }

      await refetch();
      showToastMessage(`Availability ${editAvailabilityPeriod?.id ? 'updated' : 'created'} successfully`);
      if (action === 'close') {
        actions.close();
      }
      loadingState.stop();
    },
    onFail: () => {
      loadingState.stop();
      showToastMessage('Something went wrong. You may try again, or report the issue.', 'error');
    },
    poolingInterval: 1000,
  });

  const handleSelect = (fieldName, setter) => (selectedProperty) => {
    const formField = getValues(fieldName);
    const newValue = formField?.includes(selectedProperty)
      ? formField?.filter((name) => name !== selectedProperty)
      : [...(formField ?? []), selectedProperty];

    setter(newValue);
    formMethods.setValue(fieldName, newValue, {
      shouldValidate: true,
    }
    )

    return newValue;
  };

  const handleSelectDaysOfTheWeek = handleSelect('daysOfTheWeek', setSelectedDays);

  const startDate = formMethods.watch('startDate');
  const endDate = formMethods.watch('endDate');
  const selectedFranchises = formMethods.watch('franchiseIds');

  const isDateExistForSelectedWeekdaysInDateRange = useMemo(() => {
    if (selectedDays.length === 0) return true;
    if (!startDate || !endDate) return true;
    const parsedStartDate = new Date(`${startDate}T00:00:00`);
    const parsedEndDate = new Date(`${endDate}T23:59:59`);
    if (parsedEndDate < parsedStartDate) return false;
    const daysInRange = eachDayOfInterval({
      end: parsedEndDate,
      start: parsedStartDate,
    });
    const daysOfWeekPresent = selectedDays.reduce((accumulator, dayOfWeek) => {
      accumulator[dayOfWeek] = daysInRange.some(
        (date) => getDay(date) === dayOfWeek
      );
      return accumulator;
    }, {});
    return Object.values(daysOfWeekPresent).every(Boolean);
  }, [endDate, selectedDays, startDate]);

  const onSubmit = (action, allowConflicts = false) => async (values) => {
    const { daysOfTheWeek, endDate, endTime, startDate, startTime,franchiseIds } = values;

    if (!isDateExistForSelectedWeekdaysInDateRange) {
      showToastMessage('Please select a date range that includes the selected days of the week', 'info');
      return;
    }
    try {
      loadingState.start();
      const input = daysOfTheWeek.map((day) => {
        return {
          allowConflicts,
          coachId,
          dayOfWeek: day,
          endDate,
          endTime,
          id: editAvailabilityPeriod?.id ?? null,
          startDate,
          startTime,
          franchiseIds,
          franchiseIdsToRemove: periodFranchises
            .filter((f) => !franchiseIds.includes(f.id))?.map((f) => f.id)||[],
        };
      });
      const response = await upsertAvailabilityPeriod({
        variables: {
          input,
        },
      });
      if (response.data?.upsertAvailabilityPeriod?.id) {
        setAsyncTaskId(Number(response.data?.upsertAvailabilityPeriod?.id));
        setAsyncTaskExtraData({
          action,
          values,
        });
      } else {
        throw new Error('Upsert availability period failed');
      }
    } catch {
      loadingState.stop();
    }
  };

  const showForm = useMemo(() => {
    //show form if atleast one of the franchise allow coaches to manage their availability
     return franchises.some(
    (f) => f?.settings?.canManageAvailability
  );
   }, [
    franchises
  ]) 
  return (
    <Modal className="tw-max-w-[52rem]" $title={title} $actions={actions}>
      <Modal.Content className="tw-text-[16px]">
        {showForm ?
          <FormProvider {...formMethods}>
            {
              coachAllFranchises?.length > 1 && (
                <>
                  <p className="tw-font-semibold">
                    {isEdit
                      ? "Availability associated with"
                      : formFields.franchiseIds.label}
                  </p>
                  <div className="tw-border-b tw-pb-3">
                    {coachAllFranchises.map((item) => {
                      return (
                        <div key={item.id}>
                          <FormControlLabel
                            checked={selectedFranchises.includes(item.id)}
                            control={<Checkbox color="primary" />}
                            disabled={
                              upsertAvailabilityPeriodLoading ||
                              loadingState.get ||
                              item?.disabled
                            }
                            label={item.name}
                            className="tw-mr-0 tw-text-[14px]"
                            onChange={() => {
                              const previousValues = getValues("franchiseIds");
                              const newValue = previousValues.includes(item.id)
                                ? previousValues.filter((name) => name !== item.id)
                                : [...(previousValues ?? []), item.id];
                              formMethods.setValue("franchiseIds", newValue, {
                                shouldValidate: true,
                              });
                            }}
                          />
                          {item?.disabled ? (
                            <span className="tw-font-normal tw-text-base tw-leading-6 tw-tracking-normal"
                              style={{
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              color: "rgba(0, 0, 0, 0.38)"
                            }}>
                              {" "}
                              (Please contact your manager to update your availability)
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      );
                    })}
                    {errors?.franchiseIds && (
                      <Error message={errors?.franchiseIds?.message} />
                    )}
                  </div>
                </>
              )
            }
            <div style={{ marginBottom: '1rem' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedDays.length === 7}
                    color="primary"
                    indeterminate={selectedDays.length > 0 && selectedDays.length < 7}
                    onChange={() => {
                      setSelectedDays(
                        selectedDays.length === formFields.daysOfTheWeek.options.length
                          ? []
                          : formFields.daysOfTheWeek.options.map((item) => item.value)
                      );
                      formMethods.setValue(
                        'daysOfTheWeek',
                        selectedDays.length === formFields.daysOfTheWeek.options.length
                          ? []
                          : formFields.daysOfTheWeek.options.map((item) => item.value),
                        {
                          shouldValidate: true,
                        }
                      );
                    }}
                  />
                }
                className="tw-text-[14px]"
                disabled={upsertAvailabilityPeriodLoading || loadingState.get || isEdit}
                label="All days of the Week"
              />
              <div>
                {formFields.daysOfTheWeek.options.map((item) => (
                  <FormControlLabel
                    key={item.label}
                    checked={selectedDays.includes(item.value)}
                    control={<Checkbox color="primary"
                    />}
                    className="tw-text-[14px]"
                    disabled={upsertAvailabilityPeriodLoading || loadingState.get || isEdit}
                    label={item.label}
                    onChange={() => {
                      handleSelectDaysOfTheWeek(item.value);
                      formMethods.trigger('daysOfTheWeek');
                    }}
                  />
                ))}
                {errors?.daysOfTheWeek && <Error message={errors?.daysOfTheWeek?.message} />}
                {!isDateExistForSelectedWeekdaysInDateRange && (
                  <Error message="Please select a date range that includes the selected days of the week" />
                )}
              </div>
            </div>

            <InputField
              {...register('startDate')}
              className="mb-12"
              type="date"
              $label="Start Date"
              error={errors?.startDate?.message}
            />
            <InputField
              {...register('endDate')}
              type="date"
              className="mb-12"
              $label="End Date"
              error={errors?.endDate?.message}
            />
            <InputField
              {...register('startTime')}
              type="time"
              className="mb-12"
              $label="Start Time"
              error={errors?.startTime?.message}
            />
            <InputField
              {...register('endTime')}
              type="time"
              className="mb-12"
              $label="End Time"
              error={errors?.endTime?.message}
            />
         
          </FormProvider>
          : (<Alert severity="info">
              <Typography variant="body1">Please contact your manager to update your availability</Typography>
          </Alert>
          )}
      </Modal.Content>
     <Modal.Footer style={{display:"flex"}} className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-1 tw-items-center sm:tw-justify-end">
  {showForm && (
    <div className="tw-flex tw-gap-1 tw-w-full sm:tw-justify-end sm:tw-w-auto">
      <Button
        $loading={loadingState.get || upsertAvailabilityPeriodLoading}
        disabled={loadingState.get || upsertAvailabilityPeriodLoading || Object.keys(errors).length > 0}
        onClick={(e) => {
          formMethods.handleSubmit(onSubmit('continue'))(e);
        }}
        type="button"
        className="tw-w-full sm:tw-w-auto"
      >
        {upsertAvailabilityPeriodLoading || (loadingState.get ? <CircularProgress size={22} /> : "Save & Continue")}
      </Button>

      <Button
        $loading={loadingState.get || upsertAvailabilityPeriodLoading}
        disabled={loadingState.get || upsertAvailabilityPeriodLoading || Object.keys(errors).length > 0}
        onClick={(e) => {
          formMethods.handleSubmit(onSubmit('close'))(e);
        }}
        type="button"
        className="tw-w-full sm:tw-w-auto"
      >
        {upsertAvailabilityPeriodLoading || (loadingState.get ? <CircularProgress size={22} /> : "Save & Close")}
      </Button>
    </div>
  )}

  <Button
    onClick={actions.close}
    type="button"
    className="outline tw-w-full tw-mt-1 sm:tw-mt-0 sm:tw-ml-2 sm:tw-w-auto tw-outline tw-text-red tw-border-red"
  >
    Cancel
  </Button>
</Modal.Footer>


      {isOpenConflictingModal && (
        <ConfirmConflictingDates
        key={conflictingModalKey}
        actions = {ConflictingModalActions}
        conflictingPeriods={selectedItem?.conflictingPeriods}
        onClose={() => {
        ConflictingModalActions.close();
        loadingState.stop();
        }}
        onConfirm={() => {
        ConflictingModalActions.close();
        onSubmit(selectedItem.action, true)(selectedItem?.values);
        }}
        />
      )}
    </Modal>
  );
};

export default AddAvailability;
